/* eslint-disable camelcase */

import { SortingDirection } from '@lightspeed/cirrus-table/src/features/Sortable/types';

/**
 * API
 * ============================================================================
 */
export type Purchase = {
  orderID: number;
  archived: boolean;
  arrivalDate?: Date;
  b2bOrderNumber: string;
  b2bOrderUID: string;
  complete: boolean;
  costsModifiedAfterShipment: boolean;
  createdByEmployeeID: number;
  createTime: Date;
  discount: number;
  discountIsPercent: boolean;
  discountMethod: 'evenly' | 'proportionally' | 'total';
  discountMoneyValue: number;
  discountMoneyVendorValue: number;
  discountPercentValue: number;
  hasShipments: boolean;
  noteID: number;
  orderedDate?: Date;
  otherCost: number;
  otherVendorCost: number;
  receivedDate?: Date;
  refNum: string;
  shipCost: number;
  shipInstructions: string;
  shippingCostMethod: 'evenly' | 'proportionally' | 'total';
  shipVendorCost: number;
  shopID: number;
  stockInstructions: string;
  subTotalCost: number;
  timeStamp: Date;
  totalCost: number;
  totalDiscount: number;
  totalQuantity: number;
  vendorCurrencyCode: string;
  vendorCurrencyRate: number;
  vendorID: number;
  Note?: {
    noteID: number;
    note: string;
    isPublic: boolean;
    timeStamp: Date;
  };
};

export type PurchaseRequest = Omit<Purchase, 'arrivalDate' | 'orderedDate' | 'receivedDate'> & {
  arrivalDate?: string;
  orderedDate?: string;
  receivedDate?: string;
};

export type PurchaseLine = {
  orderLineID: number;
  checkedIn: number;
  createTime: Date;
  discountMoneyValue: number;
  discountMoneyVendorValue: number;
  discountPercentValue: number;
  itemID: number;
  numReceived: number;
  orderID: number;
  originalPrice: number;
  price: number;
  quantity: number;
  shippingCost: number;
  shippingVendorCost: number;
  timeStamp: Date;
  total: number;
  vendorCost: number;
};

export type Item = {
  itemID: number;
  archived: boolean;
  avgCost: number;
  categoryID: number;
  createTime: Date;
  customSku: string;
  defaultCost: number;
  defaultVendorID: number;
  departmentID: number;
  description: string;
  discountable: boolean;
  ean: string;
  itemMatrixID: number;
  itemType: 'default' | 'non_inventory' | 'serialized' | 'box' | 'serialized_assembly' | 'assembly';
  manufacturerID: number;
  manufacturerSku: string;
  modelYear: number;
  publishToEcom: boolean;
  seasonID: number;
  serialized: boolean;
  systemSku: number;
  tax: boolean;
  taxClassID: number;
  timeStamp: Date;
  upc: string;
  ItemVendorNums: ItemVendorNum[];
};

export type RawItemResponse = Item & {
  ItemVendorNums: {
    ItemVendorNum: ItemVendorNum | ItemVendorNum[];
  };
};

export type ItemVendorNum = {
  itemVendorNumID: number;
  b2bCatalogUUID: string;
  cost: number;
  itemID: number;
  timeStamp: Date;
  value: string;
  vendorID: number;
};

export type UpdateItemVendorNumRequest = {
  value: string;
  b2bCatalogUUID?: string;
};

export type Vendor = {
  vendorID: number;
  accountNumber: string;
  archived: boolean;
  b2bSellerUID: string;
  name: string;
  priceLevel: string;
  purchasingCurrency: VendorPurchasingCurrency;
  shareSellThrough: boolean;
  timeStamp: Date;
  updateCost: boolean;
  updateDescription: boolean;
  updatePrice: boolean;
};

export type VendorPurchasingCurrency = {
  code: string;
  symbol: string;
  rate: number;
};

export type VendorShop = {
  vendorShopID: number;
  b2bShippingAddressUID: string;
  createTime: Date;
  shopID: number;
  timeStamp: Date;
  vendorID: number;
};

export type NuorderProduct = {
  attributes: {
    key: string;
    value: string;
    displayName: string;
  }[];
  brand_id: string;
  brand_name: string;
  category: string;
  color: string;
  custom_attributes: Record<string, string>;
  global_group_id: string;
  group_id: string;
  id: string;
  images?: {
    id: string;
    image_url: string;
  }[];
  name: string;
  season: string;
  seller_id: string;
  seller_name?: string;
  size: string;
  size_order?: number;
  supplier_id: string;
  upc: string;
  variant_count: string;
  varies_by: {
    dimension: string[];
  }[];
};

export type NuorderProductOrderable = NuorderProduct & {
  inventory?: {
    display_name?: string;
    period_start?: number;
    prebook?: boolean;
    quantity?: number;
    warehouse_id: string;
  }[];
  is_nuorder: boolean;
  orderable_source: boolean;
  pricing?: {
    amount: string;
    currency: string;
    source: string;
    type: string;
  }[];
};

export type NuorderOrderRequest = {
  currency_code: string; // ISO 4217 currency code, e.g., "USD"
  line_items: NuorderOrderLineItemRequest[];
  shipping_address_id?: string;
  customer_po_number?: string;
  draft?: boolean; // Optional, defaults to false
  notes?: string; // Optional, additional notes for the order
  ship_start?: number; // Optional, timestamp for the start of shipping
};

export type NuorderOrderLineItemRequest = {
  product_id: string;
  quantity: number;
  warehouse_id?: string; // Optional, if not provided, the default warehouse will be used
};

export type NuorderOrderStatus =
  | 'approved'
  | 'archived'
  | 'cancelled'
  | 'draft'
  | 'edited'
  | 'pending'
  | 'processed'
  | 'review'
  | 'shipped'
  | 'working';

export type NuorderOrder = {
  order_id: string;
  archived: boolean;
  brand_id: string;
  billing_address: {
    city: string;
    country: string;
    line_1: string;
    line_2: string;
    state: string;
    zip: string;
  };
  brand_name: string;
  created_on: number; // timestamp in seconds
  creator: string;
  currency_code: string; // ISO 4217 currency code, e.g., "USD"
  customer_po_number: string;
  edited: boolean;
  line_items: {
    original_price: number;
    price: number;
    product_id: string;
    quantity: number;
    ship_end: number;
    ship_start: number;
    warehouse_id: string;
  }[];
  locked: boolean;
  modified_on: number;
  notes: string;
  order_group_id: string;
  order_number: string;
  retailer: {
    buyer: string;
    buyer_name: string;
    ref: string;
    retailer_name: string;
  };
  ship_end: number;
  ship_start: number;
  shipping_address: {
    city: string;
    country: string;
    line_1: string;
    line_2: string;
    state: string;
    zip: string;
  };
  split: boolean;
  status: NuorderOrderStatus;
  total: number;
  total_line_items: number;
  total_line_items_quantity: number;
  total_units_quantity: number;
};

export type NuorderBrandAddress = {
  id: string;
  warehouse_id?: string;
  display_name?: string;
  type: 'both' | 'billing' | 'shipping';
  line_1: string;
  line_2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country: string;
  phone?: string;
  default_shipping: boolean;
  default_billing: boolean;
  billing_code: string;
  shipping_code: string;
  external_reference: null | string;
};

export type NuorderBrand = {
  id: string;
  name: string;
  companies: {
    id: string;
    name: string;
    warehouse_id: string;
    addresses: NuorderBrandAddress[];
  }[];
};

/**
 * UI
 * ============================================================================
 */
export const ItemReadiness = {
  NO_UPC: 1,
  NO_UUID: 2,
  NOT_ON_NUORDER: 3,
  NO_AVAILABILITY: 4,
  OUT_OF_STOCK: 5,
  OVER_ORDERED: 6,
  NOT_READY: 7,
  READY: 8,
} as const;

export type ItemReadiness = (typeof ItemReadiness)[keyof typeof ItemReadiness];

export type RowData = {
  id: number;
  purchaseID: number;
  itemID: number;
  vendorID: number;
  upc: string;
  description: string;
  itemVendorNum: string;
  b2bCatalogUUID: string;
  qty: number;
  total: number;
};

export type RowDataSortable = RowData & {
  availability: string | number;
  itemReadiness: ItemReadiness;
};

export type TableFilter<T> = {
  key: keyof T;
  values: T[keyof T][];
};

export type Configs = {
  b2bWebServiceBaseURI: string;
};

export type SortingPreference = {
  sortKey: keyof RowDataSortable;
  sortDirection: SortingDirection;
};

export const DefaultSortingPreference: SortingPreference = {
  sortKey: 'id',
  sortDirection: 'unsorted',
} as const;

export type Counts = { all: number; error: number; warning: number; ready: number };
